import { styled } from "@linaria/react"
import { useEffect, useRef, useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input } from "@/components/Input/Input.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CheckmarkLight, CloseLight, CreateRegular, DeleteLight, EditLight, Icon16, Icon20 } from "@/icons/Icons.ts"
import { SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { AddSecretDialog } from "@/routes/projects/secrets/AddSecretDialog.tsx"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { ProjectSecret } from "@/lib/client/generated/TypeDefinitions.ts"
import type { ChangeEvent, KeyboardEvent } from "react"

const ButtonWithIcon = styled(Button)`
  gap: 4px;
`

interface SecretItemProps {
  secret: ProjectSecret
  onDelete: (name: string) => Promise<void>
  onValueChange: (name: string, value: string) => Promise<void>
  withExplicitEditMode: boolean
  canEditProject: boolean
  disabled: boolean
  error?: string | null
}

const SecretItem = (
  {
    secret,
    onDelete,
    onValueChange,
    withExplicitEditMode,
    canEditProject,
    disabled,
    error,
  }: SecretItemProps
) => {
  const [value, setValue] = useState("")
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [tempValue, setTempValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const handleValueChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    if (withExplicitEditMode) {
      setTempValue(newValue)
    } else {
      setValue(newValue)
      await onValueChange(secret.key, newValue)
    }
  }

  const handleEditClick = () => {
    if (withExplicitEditMode && !disabled && !editing) {
      setEditing(true)
      setTempValue(value)
    }
  }

  const handleSave = async () => {
    if (withExplicitEditMode && tempValue !== "") {
      setValue(tempValue)
      await onValueChange(secret.key, tempValue)
      setEditing(false)
    }
  }

  const handleCancel = () => {
    if (withExplicitEditMode) {
      setTempValue(value)
      setEditing(false)
    }
  }

  const handleKeyDown = async (e: KeyboardEvent) => {
    if (withExplicitEditMode && editing) {
      if (e.key === "Enter") {
        e.preventDefault()
        await handleSave()
      } else if (e.key === "Escape") {
        e.preventDefault()
        handleCancel()
      }
    }
  }

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editing])

  return (
    <SecretItemContainer>
      <Vertical gap="8px">
        <Horizontal justifyContent="space-between" alignItems="flex-start">
          <Horizontal alignItems="center" gap="8px">
            <EllipsisContainer>
              <Tooltip content={secret.key}>
                <SecretName>{secret.key}</SecretName>
              </Tooltip>
            </EllipsisContainer>
          </Horizontal>
          {!disabled && canEditProject && (
            <Tooltip content="Delete secret">
              <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
                trigger={(
                  <DeleteButton kind="icon" onClick={() => setConfirmDialogOpen(true)}>
                    <DeleteLight />
                  </DeleteButton>
                )}
                description="Are you sure you want to delete this secret?"
                confirmText={deleting ? ellipsize("Deleting") : "Delete"}
                disabled={deleting}
                onConfirm={async () => {
                  try {
                    setDeleting(true)
                    await onDelete(secret.key)
                    setConfirmDialogOpen(false)
                  } finally {
                    setDeleting(false)
                  }
                }}
              />
            </Tooltip>
          )}
        </Horizontal>

        <EllipsisContainer>
          <Tooltip content={secret.description}>
            <SecretDescription>
              {secret.description}
            </SecretDescription>
          </Tooltip>
        </EllipsisContainer>

        <Vertical gap="4px">
          <Horizontal gap="8px" alignItems="center">
            <SecretInputContainer>
              <StyledInput
                value={(withExplicitEditMode && editing) ? tempValue : value}
                onChange={handleValueChange}
                placeholder={(withExplicitEditMode && !value) ? SECRET_VALUE_PLACEHOLDER : "Enter secret value"}
                type="password"
                disabled={disabled || !canEditProject || (withExplicitEditMode && !editing)}
                onKeyDown={handleKeyDown}
                ref={inputRef}
              />
              {withExplicitEditMode && (
                <ActionButtonsContainer>
                  {!editing && !disabled && canEditProject && (
                    <Tooltip content="Edit secret value">
                      <InlineActionButton kind="icon" onClick={handleEditClick}>
                        <EditLight className={Icon20} />
                      </InlineActionButton>
                    </Tooltip>
                  )}
                  {editing && (
                    <>
                      <Tooltip content="Save changes">
                        <InlineActionButton kind="icon" onClick={handleSave}>
                          <CheckmarkLight className={Icon20} color="var(--success-color)" />
                        </InlineActionButton>
                      </Tooltip>
                      <Tooltip content="Cancel editing">
                        <InlineActionButton kind="icon" onClick={handleCancel}>
                          <CloseLight className={Icon20} />
                        </InlineActionButton>
                      </Tooltip>
                    </>
                  )}
                </ActionButtonsContainer>
              )}
            </SecretInputContainer>
          </Horizontal>
          {error && <SecretValidationError>{error}</SecretValidationError>}
        </Vertical>
      </Vertical>
    </SecretItemContainer>
  )
}

interface SecretListResultProps {
  secrets: ProjectSecret[]
  onAddSecret: (key: string, description: string, value: string) => Promise<void>
  onDeleteSecret: (name: string) => Promise<void>
  onValueChange: (name: string, value: string) => Promise<void>
  onContinue?: () => void
  secretErrors?: Record<string, string | null>
  canEditProject?: boolean
  disabled?: boolean
  inChatMessage?: boolean
}

export const ProjectSecretsEditor = (
  {
    secrets,
    onAddSecret,
    onDeleteSecret,
    onValueChange,
    onContinue,
    secretErrors = {},
    canEditProject = true,
    disabled = false,
    inChatMessage = false
  }: SecretListResultProps
) => {
  const [addSecretDialogOpen, setAddSecretDialogOpen] = useState(false)

  const handleAddSecret = () => {
    setAddSecretDialogOpen(true)
  }

  // Find the first non-empty validation error for the tooltip
  const firstValidationError = Object.entries(secretErrors)
    .find(([_, error]) => error !== null && error !== "")?.[0]

  return (
    <SecretsListContainer>
      {secrets.length > 0 && (
        <SecretsList>
          {secrets.map((secret) => (
            <SecretItem
              key={secret.key}
              secret={secret}
              onDelete={onDeleteSecret}
              onValueChange={onValueChange}
              withExplicitEditMode={!onContinue}
              canEditProject={canEditProject}
              disabled={disabled}
              error={secretErrors[secret.key]}
            />
          ))}
        </SecretsList>
      )}

      {(!disabled || !inChatMessage) && (
        <AddButtonContainer>
          <ButtonWithIcon
            kind="alternative"
            onClick={handleAddSecret}
            disabled={disabled || !canEditProject}
          >
            <CreateRegular className={Icon16} />
            Add secret
          </ButtonWithIcon>
        </AddButtonContainer>
      )}

      {onContinue && !disabled && (
        <ContinueButtonContainer>
          {firstValidationError ? (
            <Tooltip content={firstValidationError}>
              <span>
                <Button
                  kind="primary"
                  onClick={onContinue}
                  disabled
                >
                  Continue
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              kind="primary"
              onClick={onContinue}
            >
              Continue
            </Button>
          )}
        </ContinueButtonContainer>
      )}

      {!disabled && (
        <AddSecretDialog
          isOpen={addSecretDialogOpen}
          onOpenChange={setAddSecretDialogOpen}
          onAddSecret={onAddSecret}
        />
      )}
    </SecretsListContainer>
  )
}

const SecretsListContainer = styled(Vertical)`
  gap: 16px;

  & > :first-child {
    margin-top: 16px;
  }
`

const SecretsList = styled(Vertical)`
  gap: 16px;
`

const SecretItemContainer = styled.div`
  padding: 12px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-8);

  background-color: var(--background-color);
`

const SecretName = styled(Ellipsis)`
  font-weight: bold;
`

const SecretDescription = styled(Ellipsis)`
  color: var(--secondary-text-color);

  ${textStyles.smallerFontSize};
`

const DeleteButton = styled(Button)`
  color: var(--secondary-text-color);

  &:hover:not(:disabled) {
    color: var(--error-color);
  }
`

const SecretInputContainer = styled(Horizontal)`
  position: relative;
  width: 100%;
`

const StyledInput = styled(Input)`
  width: 100%;
`

const AddButtonContainer = styled(Horizontal)`
  justify-content: flex-start;
`

const ContinueButtonContainer = styled(Vertical)`
  align-items: flex-end;
  gap: 8px;
`

const SecretValidationError = styled.div`
  ${textStyles.smallerFontSize};
  color: var(--error-color);
  margin-top: 2px;
`

const ActionButtonsContainer = styled(Horizontal)`
  gap: 4px;

  position: absolute;
  right: 8px;
  top: 50%;

  transform: translateY(-50%);

  background-color: var(--background-color);
  padding-left: 8px;
`

const InlineActionButton = styled(Button)`
  color: var(--secondary-text-color);
  padding: 4px;

  &:hover:not(:disabled) {
    color: var(--main-color);
  }
`
